import { X } from "@phosphor-icons/react"
import { type HTMLAttributes, type ReactNode } from "react"
import { twMerge } from "tailwind-merge"
import { IconButton, type IconButtonProps } from "../../button/IconButton"
import { Icon, type IconProps } from "../../icon/Icon"
import { Tooltip } from "../../overlays/tooltip/Tooltip"

const IconContainer = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div className={twMerge("tt-icon", className)} {...props} />
)

const MessageIcon = ({
  className,
  ...props
}: Omit<IconProps, "className"> & { className?: string }) => (
  <Icon className={twMerge("tt-icon", className)} {...props} />
)

const MessageHeading = ({
  className,
  ...props
}: HTMLAttributes<HTMLHeadingElement>) => (
  <h4 className={twMerge("tt-heading", className)} {...props} />
)

const BodyStack = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={twMerge("flex flex-col tt-body", className)} {...props} />
)

const HeaderRow = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={twMerge("flex flex-row", className)} {...props} />
)

const DismissButton = ({
  onPress,
  ...props
}: Omit<IconButtonProps, "icon" | "label">) => (
  <Tooltip content="Dismiss">
    <IconButton
      icon={X}
      label="Dismiss"
      size="small"
      variant="ghost"
      onPress={onPress}
      {...props}
    />
  </Tooltip>
)

const ContentStack = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div className={twMerge("flex flex-col w-full", className)} {...props} />
)

const ActionsRow = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div className={twMerge("flex flex-row gap-1", className)} {...props} />
)

export type BaseMessageProps = {
  actions?: ReactNode
  actionsPosition?: "inline" | "stack"
  children?: ReactNode
  className?: string
  color?: "neutral" | "green" | "yellow" | "red" | "cyan" | "violet"
  icon?: IconProps["as"]
  onDismiss?: () => void
  size?: "small" | "medium" | "large"
  title?: string
  variant?: "solid" | "muted" | "outline"
  style?: React.CSSProperties
}

export const BaseMessage = ({
  actions,
  actionsPosition = "inline",
  children,
  className,
  color = "neutral",
  icon,
  onDismiss,
  size = "medium",
  title,
  variant = "muted",
  style,
  ...props
}: BaseMessageProps) => {
  const iconAlignment = children ? "flex-start" : "center"

  return (
    <div
      data-size={size}
      data-color={color}
      data-variant={variant}
      className={twMerge("tt-message", className)}
      {...props}
      style={{
        alignItems: iconAlignment,
        ...style,
      }}
    >
      {icon && (
        <IconContainer>
          <MessageIcon as={icon} />
        </IconContainer>
      )}
      <ContentStack>
        <BodyStack>
          {title && (
            <HeaderRow>
              <MessageHeading>{title}</MessageHeading>
            </HeaderRow>
          )}
          {children && (
            <div className="text-sm leading-tight" style={{ color: "inherit" }}>
              {children}
            </div>
          )}
        </BodyStack>
        {actionsPosition === "stack" && actions && (
          <ActionsRow className="mt-3">{actions}</ActionsRow>
        )}
      </ContentStack>
      {actionsPosition === "inline" && actions && (
        <ActionsRow className="ml-3 self-center">{actions}</ActionsRow>
      )}
      {onDismiss && <DismissButton onPress={onDismiss} />}
    </div>
  )
}
